import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  APP_NAV_SHORTCUTS,
  THEME_TOGGLE_SHORTCUT_KEY,
  type AppNavShortcutKey,
} from "@/lib/appNavShortcuts";
import { isCtrlAltLetter, shouldIgnoreKeyboardShortcut } from "@/lib/keyboardShortcut";
import { useThemeStore } from "@/store/themeStore";
import { useUiStore } from "@/store/uiStore";

const NAV_SHORTCUT_KEYS = Object.keys(APP_NAV_SHORTCUTS) as AppNavShortcutKey[];

/** App-wide Ctrl+Alt navigation and theme toggle (mounted from AppLayout). */
export function useAppKeyboardShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || shouldIgnoreKeyboardShortcut(event)) return;

      if (isCtrlAltLetter(event, THEME_TOGGLE_SHORTCUT_KEY)) {
        event.preventDefault();
        event.stopPropagation();

        // Mirror the disabled switch while a page has locked the theme to light.
        const theme = useThemeStore.getState();
        if (!theme.lightLocked) theme.toggleTheme();
        return;
      }

      for (const key of NAV_SHORTCUT_KEYS) {
        if (!isCtrlAltLetter(event, key)) continue;

        event.preventDefault();
        event.stopPropagation();

        const target = APP_NAV_SHORTCUTS[key];
        if (target.clearChatwootTarget) {
          useUiStore.getState().setChatwootTarget(null);
        }
        navigate(target.path);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [navigate]);
}
